/**
 * ForkPanel — Fork & Sweep panel for creating new Flow360 cases
 * from an existing completed case with different operating conditions.
 *
 * Shows template case info, read-only config tree, and an editable
 * parameter form for submitting single forks or parameter sweeps.
 */

import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Card,
  Flex,
  Heading,
  ScrollArea,
  Select,
  Text,
} from "@radix-ui/themes";

import { useProvenanceContext } from "../context/ProvenanceContext";
import { useBranchGuard } from "../context/BranchGuardContext";
import { useProvenance } from "../hooks/useProvenance";
import { useFullConfig, useForkCase, useSweep } from "./useForkSweep";
import { SweepForm, type OperatingConditions, type ParamMode } from "./SweepForm";
import { SweepProgress } from "./SweepProgress";
import type { ForkResult } from "./types";

interface Props {
  projectId: string;
}

type NodeMeta = Record<string, unknown> | undefined;

interface CaseOption {
  nodeId: string;
  label: string;
  flow360CaseId: string;
}

const naturalCompare = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function findCompletedCases(
  nodes: Array<{ id: string; kind: string; path: string; metadata?: NodeMeta }>,
): CaseOption[] {
  const results: CaseOption[] = [];
  for (const n of nodes) {
    if (n.kind !== "run") continue;
    const meta = n.metadata as Record<string, string> | undefined;
    const caseId = meta?.flow360_case_id;
    if (!caseId || meta?.status === "error" || meta?.status === "diverged") continue;
    results.push({
      nodeId: n.id,
      label: n.path.split(".").pop() || n.id,
      flow360CaseId: caseId,
    });
  }
  return results.sort((a, b) => naturalCompare.compare(a.label, b.label));
}

function extractDefaults(config: Record<string, unknown>): {
  alpha?: number;
  beta?: number;
  mach?: number;
} {
  const oc = config.operating_condition as Record<string, unknown> | undefined;
  if (!oc) return {};
  const extract = (key: string) => {
    const val = oc[key];
    if (typeof val === "number") return val;
    if (typeof val === "object" && val && "value" in val) return (val as { value: number }).value;
    return undefined;
  };
  return { alpha: extract("alpha"), beta: extract("beta"), mach: extract("mach") };
}

const HIGHLIGHTED_KEYS = new Set([
  "operating_condition",
  "turbulence_model_solver",
  "boundaries",
  "reference_geometry",
  "freestream",
]);

function ConfigTreeNode({
  nodeKey,
  value,
  depth,
  expanded,
  onToggle,
}: {
  nodeKey: string;
  value: unknown;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
}) {
  const isObj =
    typeof value === "object" && value !== null && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isExpandable = isObj || isArray;
  const isHighlighted = depth === 0 && HIGHLIGHTED_KEYS.has(nodeKey);
  const isOpen = expanded.has(nodeKey);

  const entries = isObj
    ? Object.entries(value as Record<string, unknown>)
    : isArray
      ? (value as unknown[]).map((v, i) => [String(i), v] as [string, unknown])
      : [];

  const formatScalar = (v: unknown): string => {
    if (v === null) return "null";
    if (typeof v === "string") return `"${v}"`;
    return String(v);
  };

  return (
    <Box style={{ paddingLeft: depth > 0 ? 14 : 0 }}>
      <Text
        size="1"
        as="div"
        style={{
          cursor: isExpandable ? "pointer" : "default",
          fontWeight: isHighlighted ? 600 : 400,
          color: isHighlighted ? "var(--accent-11)" : undefined,
          whiteSpace: "nowrap",
          lineHeight: 1.6,
        }}
        onClick={() => isExpandable && onToggle(nodeKey)}
      >
        {isExpandable ? (isOpen ? "▾ " : "▸ ") : "  "}
        <span style={{ color: "var(--accent-11)" }}>{nodeKey}</span>
        {!isExpandable && (
          <span style={{ color: "var(--gray-11)", marginLeft: 6 }}>
            {formatScalar(value)}
          </span>
        )}
        {isExpandable && !isOpen && (
          <span style={{ color: "var(--gray-9)", marginLeft: 6, fontSize: 11 }}>
            {isArray
              ? `[${entries.length}]`
              : `{${entries.length}}`}
          </span>
        )}
      </Text>
      {isExpandable && isOpen && (
        <Box>
          {entries.map(([childKey, childVal]) => (
            <ConfigTreeNode
              key={childKey}
              nodeKey={childKey}
              value={childVal}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function ConfigTree({ config }: { config: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(["operating_condition"]),
  );

  const toggle = useCallback(
    (key: string) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      }),
    [],
  );

  return (
    <Box
      style={{
        fontFamily: "var(--code-font-family, monospace)",
        fontSize: 12,
      }}
    >
      {Object.entries(config).map(([key, val]) => (
        <ConfigTreeNode
          key={key}
          nodeKey={key}
          value={val}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </Box>
  );
}

function ConfigSection({ config }: { config: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const keyCount = Object.keys(config).length;

  return (
    <Box mb="3">
      <Flex
        align="center"
        gap="2"
        mb="1"
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <Text size="1" style={{ fontFamily: "monospace" }}>
          {open ? "▾" : "▸"}
        </Text>
        <Text size="2" weight="medium">
          Inherited Configuration
        </Text>
        {!open && (
          <Text size="1" color="gray">
            {keyCount} keys
          </Text>
        )}
      </Flex>
      {open && (
        <Box
          style={{
            maxHeight: 300,
            overflow: "auto",
            border: "1px solid var(--gray-a4)",
            borderRadius: 6,
            padding: 8,
          }}
        >
          <ConfigTree config={config} />
        </Box>
      )}
    </Box>
  );
}

interface SweepCaseWithParams extends ForkResult {
  params?: Record<string, number>;
}

export default function ForkPanel({ projectId }: Props) {
  const { currentBranch } = useProvenanceContext();
  const { guard } = useBranchGuard();
  const { data: provenance } = useProvenance(projectId, currentBranch);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [mode, setMode] = useState<ParamMode>("sweep");
  const [submitted, setSubmitted] = useState<SweepCaseWithParams[]>([]);

  const cases = useMemo(
    () => (provenance?.nodes ? findCompletedCases(provenance.nodes as never[]) : []),
    [provenance?.nodes],
  );

  const activeCaseId = selectedCaseId ?? cases[0]?.flow360CaseId ?? null;
  const { data: fullConfig, isLoading: configLoading } = useFullConfig(activeCaseId);

  const forkMutation = useForkCase(() => {});
  const sweepMutation = useSweep(() => {});

  const defaults = useMemo(
    () => (fullConfig?.config ? extractDefaults(fullConfig.config) : {}),
    [fullConfig?.config],
  );

  const handleSubmit = useCallback(
    (params: OperatingConditions) => {
      if (!activeCaseId) return;

      guard(async () => {
        if (mode === "single") {
          const overrides: Record<string, number> = {};
          for (const [k, arr] of Object.entries(params)) {
            if (arr?.length) overrides[k] = arr[0];
          }
          const result = await forkMutation.mutateAsync({
            base_case_id: activeCaseId,
            overrides,
            thread_project_id: projectId,
            branch: currentBranch,
          });
          setSubmitted((prev) => [...prev, result]);
        } else {
          const paramArrays: Record<string, number[]> = {};
          for (const [k, arr] of Object.entries(params)) {
            if (arr?.length) paramArrays[k] = arr;
          }
          const result = await sweepMutation.mutateAsync({
            base_case_id: activeCaseId,
            parameters: paramArrays,
            thread_project_id: projectId,
            branch: currentBranch,
          });
          setSubmitted((prev) => [...prev, ...result.cases]);
        }
      }, { hint: mode === "single" ? "fork" : "sweep" });
    },
    [activeCaseId, mode, projectId, forkMutation, sweepMutation, guard, currentBranch],
  );

  return (
    <ScrollArea style={{ height: "100%" }}>
      <Box p="3">
        <Heading size="3" mb="3">
          Solver Cockpit
        </Heading>

        {/* Case selector */}
        {cases.length > 0 && (
          <Flex align="center" gap="2" mb="3">
            <Text size="2" weight="medium">
              Template Case
            </Text>
            <Select.Root
              value={activeCaseId ?? undefined}
              onValueChange={setSelectedCaseId}
            >
              <Select.Trigger placeholder="Select a case..." />
              <Select.Content>
                {cases.map((c) => (
                  <Select.Item key={c.flow360CaseId} value={c.flow360CaseId}>
                    {c.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <Text size="1" color="gray">
              {cases.length} cases
            </Text>
          </Flex>
        )}

        {cases.length === 0 && (
          <Card mb="3">
            <Text size="2" color="gray">
              No completed cases found in this project. Import Flow360 data
              first, then use Fork & Sweep to run new cases.
            </Text>
          </Card>
        )}

        {/* Template info */}
        {fullConfig && (
          <Card mb="3" size="1">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                {fullConfig.case_name || "Case"}
              </Text>
              <Text size="1" color="gray">
                Solver: {fullConfig.solver_version || "—"} | Mesh:{" "}
                {fullConfig.volume_mesh_id?.slice(0, 8) || "—"} | Project:{" "}
                {fullConfig.project_id?.slice(0, 8) || "—"}
              </Text>
            </Flex>
          </Card>
        )}

        {/* Config tree (collapsible) */}
        {fullConfig?.config && (
          <ConfigSection config={fullConfig.config} />
        )}

        {configLoading && (
          <Text size="2" color="gray" mb="3" as="p">
            Loading configuration...
          </Text>
        )}

        {/* Sweep form */}
        {activeCaseId && !configLoading && (
          <SweepForm
            mode={mode}
            onModeChange={setMode}
            onSubmit={handleSubmit}
            submitting={forkMutation.isPending || sweepMutation.isPending}
            defaultAlpha={defaults.alpha}
            defaultBeta={defaults.beta}
            defaultMach={defaults.mach}
          />
        )}

        {/* Error display */}
        {(forkMutation.error || sweepMutation.error) && (
          <Text size="2" color="red" mt="2" as="p">
            {(forkMutation.error || sweepMutation.error)?.message}
          </Text>
        )}

        {/* Progress cards */}
        <SweepProgress cases={submitted} />
      </Box>
    </ScrollArea>
  );
}
